import { useCallback, useState } from "react";
import { PWSSessionState } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import {
  CoastMediaEpisodeNode,
  CoastMediaEpisodesList,
  CoastMediaTabConfig,
  episodesToLinkedList,
} from "./CoastMedia.util";
import { CoastMediaTab } from "./CoastMediaTab.component";
import { CoastMediaNavigation } from "./CoastMediaTabsNavigation.component";
import { CoastMediaTabSkeleton } from "./CoastMediaTabsSkeleton.component";
import { coastMediaService } from "../../../../core/services/Pws";

const log = ILog.logger("CoastMediaTabs.component");

interface CoastMediaTabsProps {
  setCurrentEpisode: (v: CoastMediaEpisodeNode, groupSlug?: string) => void;
  sessionState: PWSSessionState;
  tabs: CoastMediaTabConfig[];
  initialActiveTabIdx: number;
}

const searchTabBase: CoastMediaTabConfig = {
  episodesLL: new CoastMediaEpisodesList(),
  title: "Search results",
  categories: {},
  hasLive: false,
  slug: "search",
  resume: null,
  autoplay: true,
  enablePagination: true,
};

export const CoastMediaTabs = ({ tabs, initialActiveTabIdx, setCurrentEpisode, sessionState }: CoastMediaTabsProps) => {
  // -1 indicates search tab
  const [activeTabIdx, setActiveTabIdx] = useState(initialActiveTabIdx);
  const [fetchingSearchResults, setFetchingSearchResults] = useState(false);
  const [searchTab, setSearchTab] = useState(searchTabBase);

  const onSearch = useCallback(async (query: string) => {
    setActiveTabIdx(-1);
    setFetchingSearchResults(true);

    try {
      const { videos } = await coastMediaService.listMediaEpisodes(null, { query });
      setSearchTab({
        ...searchTabBase,
        episodesLL: episodesToLinkedList(videos),
        title: `Search results for: "${query}"`,
      });
    } catch (e) {
      log.error("Error fetching search results", e);
    } finally {
      setFetchingSearchResults(false);
    }
  }, []);

  return (
    <div className="component-coast-media-tabs">
      <CoastMediaNavigation
        setActiveTab={setActiveTabIdx}
        activeTabIdx={activeTabIdx}
        onSearch={onSearch}
        tabs={tabs}
      />
      <div className="tabs-container">
        {tabs.map((tab, idx) => (
          <CoastMediaTab
            setCurrentEpisode={setCurrentEpisode}
            isActiveTab={activeTabIdx === idx}
            sessionState={sessionState}
            key={tab.title}
            tab={tab}
          />
        ))}

        {/* Search */}
        {activeTabIdx === -1 && fetchingSearchResults ? (
          <CoastMediaTabSkeleton />
        ) : (
          <CoastMediaTab
            setCurrentEpisode={setCurrentEpisode}
            isActiveTab={activeTabIdx === -1}
            sessionState={sessionState}
            tab={searchTab}
          />
        )}
      </div>
    </div>
  );
};
